import { CheckCircleFilledIcon, Color, IconSize, LensFilledIcon, Space, VerticalFlex } from "@enxcs/dap-ui-stratos";
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { StepButton, StepIndicator, StepItem, StepList, StepPanel, StepTitle } from "./Stepper.styled";

export type StepperStepStatus = "complete" | "current" | "upcoming";

export interface StepperStep {
  id: string;
  title: string;
  content: ReactNode;
}

export interface StepperProps {
  steps: StepperStep[];
  activeStep?: number;
  defaultStep?: number;
  onStepChange?: (nextStep: number) => void;
  className?: string;
  "aria-label"?: string;
  scrollableContent?: boolean;
}

const clampStep = (index: number, stepsCount: number) => {
  if (stepsCount === 0) {
    return 0;
  }

  return Math.min(Math.max(index, 0), stepsCount - 1);
};

const getStepStatus = (index: number, currentStep: number): StepperStepStatus => {
  if (index === currentStep) {
    return "current";
  }

  if (index < currentStep) {
    return "complete";
  }

  return "upcoming";
};
const Stepper = ({ steps, activeStep, defaultStep = 0, onStepChange, className, "aria-label": ariaLabel = "Progress steps", scrollableContent }: StepperProps) => {
  const isControlled = activeStep !== undefined;
  const [uncontrolledStep, setUncontrolledStep] = useState(() => clampStep(defaultStep, steps.length));
  const panelId = useId();

  useEffect(() => {
    if (!isControlled) {
      setUncontrolledStep((current) => clampStep(current, steps.length));
    }
  }, [isControlled, steps.length]);

  const currentStep = clampStep(isControlled ? (activeStep ?? 0) : uncontrolledStep, steps.length);
  const currentPanel = steps[currentStep];

  const stepState = useMemo(
    () =>
      steps.map((step, index) => ({
        ...step,
        status: getStepStatus(index, currentStep),
      })),
    [currentStep, steps],
  );

  const handleStepSelect = (nextStep: number) => {
    const normalizedStep = clampStep(nextStep, steps.length);

    if (!isControlled) {
      setUncontrolledStep(normalizedStep);
    }

    onStepChange?.(normalizedStep);
  };

  if (!currentPanel) {
    return null;
  }

  return (
    <VerticalFlex $gap={Space.V40} className={className}>
      <StepList aria-label={ariaLabel}>
        {stepState.map((step, index) => {
          const status = step.status;

          return (
            <StepItem key={step.id}>
              <StepButton
                type="button"
                onClick={() => handleStepSelect(index)}
                aria-current={status === "current" ? "step" : undefined}
                aria-controls={status === "current" ? `${panelId}-${step.id}` : undefined}
                aria-label={step.title}
                $status={status}
              >
                <StepIndicator $status={status}>
                  {status === "complete" ? (
                    <CheckCircleFilledIcon size={IconSize.V18} tone={Color.Teal.V30} />
                  ) : (
                    <LensFilledIcon size={IconSize.V12} tone={Color.Teal.V30} />
                  )}

                  <VerticalFlex $gap={Space.V4}>
                    <StepTitle $status={status}>{step.title}</StepTitle>
                  </VerticalFlex>
                </StepIndicator>
              </StepButton>
            </StepItem>
          );
        })}
      </StepList>

      <StepPanel id={`${panelId}-${currentPanel.id}`} role="region" aria-live="polite" aria-label={currentPanel.title} $scrollable={scrollableContent}>
        {currentPanel.content}
      </StepPanel>
    </VerticalFlex>
  );
};

export default Stepper;
